import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { InviteTeamMemberForm } from './invite-team-member-form';
import { ModalDialog } from '../../ui';
import { usersApi, ISeatPreview } from '../../api';
import { handleFailedRequest, notify } from '../../util';
import { IResponseError } from '../../data-access';
import { RootState } from '../../store';
import { useUser } from '../../hooks';

interface IInviteTeamMembersModalProps {
  onClose: () => void;
  onSuccess?: (email: string) => void;
}

export const InviteTeamMembersModal = ({ onClose, onSuccess }: IInviteTeamMembersModalProps): JSX.Element => {
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [seatPreview, setSeatPreview] = useState<ISeatPreview | null>(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState<boolean>(false);
  const { currentUser } = useUser();

  const teamMembers = useSelector((state: RootState) => state.team.members);
  const billingStatus = currentUser?.billing_status;
  const isTrial = billingStatus === 'trial';
  const isActive = currentUser?.has_active_subscription;
  const totalMembers = teamMembers?.length || currentUser?.teammates || 0;
  const paidMembers = currentUser?.teammates_paid || 0;
  const totalSeats = currentUser?.active_licenses_count || 0;
  const seatsAvailable = totalSeats - paidMembers;

  const needsSeatPurchase = !!isActive && !isTrial && seatsAvailable <= 0;

  useEffect(() => {
    if (needsSeatPurchase) {
      setIsLoadingPreview(true);
      usersApi.previewSeatCost()
        .then(setSeatPreview)
        .catch(() => setSeatPreview(null))
        .finally(() => setIsLoadingPreview(false));
    }
  }, [needsSeatPurchase]);

  const handleFormSubmit = (formFields: { email: string }): void => {
    setIsProcessing(true);

    usersApi.inviteTeamMember(formFields.email)
      .then(() => {
        onClose();
        onSuccess && onSuccess(formFields.email);
        notify('success',  'Invitation sent successfully');
      })
      .catch((error: IResponseError) => {
        handleFailedRequest(error.status, error.text);
      })
      .finally(() => setIsProcessing(false));
  }

  const handleClose = (): void => {
    if (!isProcessing) {
      onClose();
    }
  }

  return (
    <ModalDialog
      className="invite-team-member-modal"
      title="Invite a new team member"
      isOpen={true}
      onCancel={handleClose}
    >
      <InviteTeamMemberForm
        isLoading={isProcessing}
        onSuccess={handleFormSubmit}
        isTrial={isTrial}
        isActive={!!isActive}
        totalMembers={totalMembers}
        seatsAvailable={Math.max(seatsAvailable, 0)}
        currentUserEmail={currentUser?.email}
        seatPreview={seatPreview}
        isLoadingPreview={isLoadingPreview}
      />
    </ModalDialog>
  );
}

InviteTeamMembersModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
};
